import React, { useState } from 'react'
import { isEmpty } from 'lodash'
import clsx from 'clsx'
import { camelCase } from 'lodash'
import { useHistory } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useTranslation } from 'react-i18next'

import { loaderState, breadcrumbState, openModalState } from 'recoils/atoms'
import { LoaderCover, Breadcrumb, Navbar } from 'components'
import Menu, { MenuItem } from 'components/Menu'
import { IconLogo, IconHamburger } from 'components/Icons'
import { routes } from 'helpers/routes'
import { getSessionStorage, setSessionStorage } from 'helpers/common'

import packages from '../../../package.json'

interface IBackOfficeLayout {
  children: React.ReactNode
}

const BackOfficeLayout: React.FC<IBackOfficeLayout> = (
  props: IBackOfficeLayout,
): JSX.Element => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(
    getSessionStorage('isMenuOpen')
      ? Boolean(getSessionStorage('isMenuOpen') === 'true')
      : true,
  )

  const { children } = props
  const history = useHistory()
  const [breadcrumbs] = useRecoilState(breadcrumbState)
  const isLoading = useRecoilValue(loaderState)
  const isOpenModal = useRecoilValue(openModalState)
  const { t } = useTranslation(['layout', 'common'])

  const base = 'l-backoffice'
  const regex = /\/(\w+-?\w+)\/?/g
  const mainPath = history.location.pathname.match(regex)?.[0]
  const currentPath = mainPath && mainPath.replaceAll('/', '')
  const _handleNavigate = (key: string): void => {
    const v3path = window._env_.REACT_APP_V3_APP_URL
    switch (key) {
      case routes.ticket.id:
        if (isEmpty(history.location.search)) {
          history.push(`/${key}`)
        } else {
          const defaultSearch = '?_sort=code&_order=DESC&_page=1'

          window.location.replace(`${routes.ticket.path}${defaultSearch}`)
        }

        break

      case 'dashboard':
        window.location.replace(`${v3path}/dashboard`)
        break
      case 'technicianSchedule':
        window.location.replace(`${v3path}/technician-schedule`)
        break
      case 'issueTypeMangement':
        window.location.replace(`${v3path}/issue-type`)
        break
      case 'reviewAndRating':
        window.location.replace(`${v3path}/feedback`)
        break
      default:
        history.push(`/${key}`)
        break
    }
  }

  const onMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen)
    setSessionStorage('isMenuOpen', `${!isMenuOpen}`)
  }

  const renderContentSidebar = () => {
    const menuList = isMenuOpen && (
      <Menu
        onChange={_handleNavigate}
        defaultSelected={camelCase(currentPath)}
        defaultOpenKeys={['skuList']}
      >
        <MenuItem id='dashboard'>
          {t('layout:analyticsDashboard.breadcrumb')}
        </MenuItem>
        <MenuItem id='technicianSchedule'>
          {t('layout:technicianSchedule.breadcrumb')}
        </MenuItem>
        <MenuItem id='issueTypeMangement'>
          {t('layout:issueTypeManagement.breadcrumb')}
        </MenuItem>
        <MenuItem id='reviewAndRating'>
          {t('layout:reviewAndRaiting.breadcrumb')}
        </MenuItem>
        <MenuItem id={routes.ticket.id}>
          {t('layout:ticketManagement.breadcrumb')}
        </MenuItem>
        <MenuItem id={routes.company.id}>
          {t('layout:companyManagement.breadcrumb')}
        </MenuItem>
        <MenuItem id={routes.branch.id}>
          {t('layout:branchManagement.breadcrumb')}
        </MenuItem>
        <MenuItem id={routes.product.id}>
          {t('layout:skuManagement.breadcrumb')}
        </MenuItem>
      </Menu>
    )

    const footer = isMenuOpen && (
      <div className='l-backoffice__footer'>
        <span data-testid='l-backoffice-license'>
          {t('common:license.label')}
          {packages.version}
        </span>
      </div>
    )

    return (
      <>
        <div>
          <div
            className={clsx('navbar__logo', isMenuOpen && 'navbar__logo--open')}
          >
            <IconLogo className={`navbar__logo-brand`} />
            <IconHamburger
              className='navbar__hamburger'
              variants='md'
              onClick={onMenuToggle}
            />
          </div>

          {menuList}
        </div>

        {footer}
      </>
    )
  }

  return (
    <div className={clsx(base, isOpenModal && `${base}--fixed`)}>
      <LoaderCover isLoading={isLoading} />
      <div
        className={clsx(
          `${base}__sidebar`,
          isMenuOpen && `${base}__sidebar--open`,
        )}
      >
        {renderContentSidebar()}
      </div>
      <div
        className={clsx(`${base}__body`, isMenuOpen && `${base}__body--open`)}
      >
        <div
          className={clsx(
            `${base}__main`,
            isMenuOpen && `${base}__main--open`,
            !isMenuOpen && `${base}__main--close`,
          )}
        >
          <Navbar>
            <Breadcrumb pages={breadcrumbs} />
          </Navbar>
          <div className={`${base}__content`}>{children}</div>
        </div>
      </div>
    </div>
  )
}

export default BackOfficeLayout
